import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { signInWithPopup, onAuthStateChanged } from 'firebase/auth';
import { auth as firebaseAuth, googleProvider } from '../config/firebase';
import {
  ArrowLeft,
  Star,
  Check,
  X,
  ExternalLink,
  HardHat,
  Loader2,
  AlertCircle,
  Globe,
  Monitor,
  Smartphone,
  GitCompare,
  MessageSquare,
  Shield,
  Minus,
  ChevronDown,
  ChevronUp,
  Zap,
  Building2,
  Briefcase,
  Plus,
  Layers,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  fetchToolBySlug,
  fetchToolFeatures,
  fetchToolFeaturesMany,
  fetchToolReviews,
  fetchToolCategories,
  fetchToolPlans,
  submitToolReview,
} from '../services/aecService';
import { useCompareList } from '../hooks/useCompareList';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const PRICING_LABEL = {
  free: 'Free',
  freemium: 'Freemium',
  paid: 'One-time',
  subscription: 'Subscription',
  quote: 'Quote',
};

const PRICING_BADGE = {
  free:         'bg-emerald-50 text-emerald-700 border-emerald-200',
  freemium:     'bg-teal-50 text-teal-700 border-teal-200',
  paid:         'bg-blue-50 text-blue-700 border-blue-200',
  subscription: 'bg-violet-50 text-violet-700 border-violet-200',
  quote:        'bg-amber-50 text-amber-700 border-amber-200',
};

const PLATFORM_ICON = {
  web:     <Globe className="w-3.5 h-3.5" />,
  windows: <Monitor className="w-3.5 h-3.5" />,
  mac:     <Monitor className="w-3.5 h-3.5" />,
  ios:     <Smartphone className="w-3.5 h-3.5" />,
  android: <Smartphone className="w-3.5 h-3.5" />,
  linux:   <Monitor className="w-3.5 h-3.5" />,
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------
function getSessionId() {
  let id = sessionStorage.getItem('aec_session_id');
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem('aec_session_id', id);
  }
  return id;
}

async function trackEvent(toolId, eventType) {
  try {
    await fetch(
      `${process.env.REACT_APP_SUPABASE_DEFAULT_URL}/functions/v1/track-event`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_DEFAULT_ANON_KEY}`,
        },
        body: JSON.stringify({
          tool_id:    toolId,
          event_type: eventType,
          session_id: getSessionId(),
          referrer:   document.referrer,
        }),
      }
    );
  } catch {
    // silent — analytics must never break the page
  }
}

// ---------------------------------------------------------------------------
// Display name resolver
// ---------------------------------------------------------------------------
function getDisplayName(review) {
  const dm = review.display_name;
  if (!dm || dm === 'anonymous') return 'Anonymous reviewer';
  if (dm === 'first_initial_last') {
    const parts = (review.reviewer_name_input || '').trim().split(/\s+/);
    if (parts.length >= 2 && parts[0])
      return `${parts[0][0].toUpperCase()}. ${parts[parts.length - 1]}`;
    return review.reviewer_name_input || 'Anonymous reviewer';
  }
  // 'full_name' or any other value
  return review.reviewer_name_input || 'Anonymous reviewer';
}

// ---------------------------------------------------------------------------
// Shared small components
// ---------------------------------------------------------------------------

const StarRating = ({ rating, count, size = 'md' }) => {
  const star = size === 'lg' ? 'w-5 h-5' : 'w-3.5 h-3.5';
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} className={`${star} ${
            n <= Math.round(rating)
              ? 'text-amber-400 fill-amber-400'
              : 'text-gray-200 fill-gray-200'
          }`} />
        ))}
      </div>
      <span className={`${size === 'lg' ? 'text-sm' : 'text-xs'} text-gray-600 font-medium`}>
        {Number(rating).toFixed(1)}
        {count != null && <span className="text-gray-400 font-normal ml-0.5">({count})</span>}
      </span>
    </div>
  );
};

const StarPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="focus:outline-none"
      >
        <Star className={`w-6 h-6 transition-colors ${
          n <= value ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200 hover:text-amber-300'
        }`} />
      </button>
    ))}
  </div>
);


const SectionCard = ({ id, title, icon: Icon, children, cardRef }) => (
  <section id={id} ref={cardRef} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden scroll-mt-20">
    <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-2.5">
      {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    </div>
    <div className="p-6">{children}</div>
  </section>
);

// ---------------------------------------------------------------------------
// Feature value renderer
// ---------------------------------------------------------------------------
const FeatureValue = ({ feature }) => {
  if (feature.value_type === 'boolean') {
    if (feature.value_boolean == null)
      return <Minus className="w-4 h-4 text-gray-300" />;
    return feature.value_boolean ? (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-50">
        <Check className="w-3.5 h-3.5 text-emerald-600" />
      </span>
    ) : (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-50">
        <X className="w-3.5 h-3.5 text-red-400" />
      </span>
    );
  }
  if (feature.value_type === 'number') {
    if (feature.value_number == null) return <Minus className="w-4 h-4 text-gray-300" />;
    return (
      <span className="text-sm text-gray-700 font-medium">
        {Number(feature.value_number).toLocaleString()}
        {feature.value_unit && <span className="text-gray-400 font-normal ml-0.5">{feature.value_unit}</span>}
      </span>
    );
  }
  if (feature.value_type === 'list' && feature.value_text) {
    return (
      <div className="flex flex-wrap gap-1">
        {feature.value_text.split(',').map((v) => (
          <span key={v.trim()} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-600">
            {v.trim()}
          </span>
        ))}
      </div>
    );
  }
  if (feature.value_text)
    return (
      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-600">
        {feature.value_text}
      </span>
    );
  return <Minus className="w-4 h-4 text-gray-300" />;
};

// ---------------------------------------------------------------------------
// Review card
// ---------------------------------------------------------------------------
const ReviewCard = ({ review }) => {
  const isVerified = review.status === 'published_verified';
  const displayName = getDisplayName(review);
  const showJobTitle = review.show_job_title && review.reviewer_job_title_input;
  const showCompany  = review.show_company  && review.reviewer_company_input;
  const dateStr = (review.published_at || review.created_at)
    ? new Date(review.published_at || review.created_at)
        .toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  return (
    <div className="border border-gray-100 rounded-xl p-4 space-y-2">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900">{displayName}</p>
            {isVerified && (
              <span className="flex items-center gap-0.5 text-xs text-teal-600 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded-full font-medium">
                <Shield className="w-3 h-3" />Verified
              </span>
            )}
            {showJobTitle && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-500">
                {review.reviewer_job_title_input}
              </span>
            )}
          </div>
          {showCompany && (
            <p className="text-xs text-gray-400 mt-0.5">{review.reviewer_company_input}</p>
          )}
        </div>
        <StarRating rating={review.rating} />
      </div>

      {/* Title */}
      {review.title && (
        <p className="text-sm font-semibold text-gray-800">{review.title}</p>
      )}

      {/* Body */}
      {review.body && (
        <p className="text-sm text-gray-600 leading-relaxed">{review.body}</p>
      )}

      {/* Pros / Cons */}
      {(review.pros || review.cons) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
          {review.pros && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              <p className="text-xs font-semibold text-emerald-700 mb-0.5">Pros</p>
              <p className="text-xs text-emerald-700 leading-relaxed">{review.pros}</p>
            </div>
          )}
          {review.cons && (
            <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <p className="text-xs font-semibold text-red-600 mb-0.5">Cons</p>
              <p className="text-xs text-red-600 leading-relaxed">{review.cons}</p>
            </div>
          )}
        </div>
      )}

      {dateStr && <p className="text-xs text-gray-300">{dateStr}</p>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Variations comparison table (parent vs child plans/addons)
// ---------------------------------------------------------------------------
const RECURRING_BILLING = new Set(['annual', 'yearly', 'monthly', 'quarterly', 'biannual']);
const resolveModel = (model, billing) =>
  model === 'paid' && RECURRING_BILLING.has(billing) ? 'subscription' : model;

const VPricingCell = ({ model, billing }) => {
  if (!model) return <Minus className="w-4 h-4 text-gray-300 mx-auto" />;
  const m = resolveModel(model, billing);
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${PRICING_BADGE[m] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>
      {PRICING_LABEL[m] ?? m}
    </span>
  );
};

const VPriceCell = ({ price, currency, pricingModel }) => {
  const isFreeModel = pricingModel === 'free' || pricingModel === 'freemium';
  const isPaidModel = pricingModel && !isFreeModel;
  if (price > 0) return <span className="text-sm font-medium text-gray-900">{currency ?? 'USD'} {Number(price).toLocaleString()}</span>;
  if (isFreeModel) return <span className="text-sm font-medium text-emerald-600">Free</span>;
  if (isPaidModel) return <span className="text-sm font-medium text-amber-700">Contact Sales</span>;
  return <span className="text-sm text-gray-400">—</span>;
};

const VBoolCell = ({ value }) => {
  if (value == null) return <Minus className="w-4 h-4 text-gray-300 mx-auto" />;
  return value
    ? <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-50 mx-auto"><Check className="w-3.5 h-3.5 text-emerald-600" /></span>
    : <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-50 mx-auto"><X className="w-3.5 h-3.5 text-red-400" /></span>;
};

const VariationsTable = ({ tool, plans, planFeatures = {}, compareList, onToggleCompare }) => {
  if (!plans.length) return null;

  // ── Column visibility state ──────────────────────────────────
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const toggleCol = (id) =>
    setHiddenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ── Drag-to-scroll (middle or left mouse button) ─────────────
  const scrollRef = useRef(null);
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0 });

  const onMouseDown = (e) => {
    if (e.button !== 0 && e.button !== 1) return;
    if (e.button === 1) e.preventDefault(); // suppress middle-click autoscroll
    drag.current = { active: true, startX: e.pageX, scrollLeft: scrollRef.current.scrollLeft };
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.userSelect = 'none';
  };
  const onMouseMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.pageX - drag.current.startX;
    scrollRef.current.scrollLeft = drag.current.scrollLeft - dx;
  };
  const onMouseUp = () => {
    if (!drag.current.active) return;
    drag.current.active = false;
    scrollRef.current.style.cursor = 'grab';
    scrollRef.current.style.userSelect = '';
  };

  // Build uniform column descriptors — parent tool first, then child plans
  const allColumns = [
    {
      id: tool.id, slug: tool.slug, name: tool.name,
      tool_type: 'product', is_primary: false,
      pricing_model: tool.pricing_model,
      price: tool.price_from,
      currency: tool.price_currency ?? 'USD',
      billing: tool.billing_cycle,
      has_free_trial: tool.has_free_trial,
      trial_days: tool.trial_days,
    },
    ...plans.map((p) => ({
      id: p.id, slug: p.slug, name: p.name,
      tool_type: p.tool_type,
      is_primary: p.is_primary,
      pricing_model:  p.tool_meta?.pricing_model ?? null,
      price:          p.tool_meta?.price_from     ?? null,
      currency:       p.tool_meta?.price_currency ?? 'USD',
      billing:        p.tool_meta?.billing_cycle  ?? null,
      has_free_trial: p.tool_meta?.has_free_trial ?? null,
      trial_days:     p.tool_meta?.trial_days     ?? null,
    })),
  ];
  const columns = allColumns.filter((c) => !hiddenIds.has(c.id));

  const TYPE_BADGE = {
    product: 'bg-gray-100 text-gray-600 border-gray-200',
    plan:    'bg-violet-50 text-violet-700 border-violet-200',
    addon:   'bg-amber-50 text-amber-700 border-amber-200',
  };

  const pricingRows = [
    { label: 'Type',          render: (c) => <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${TYPE_BADGE[c.tool_type] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>{capitalize(c.tool_type ?? '—')}</span> },
    { label: 'Pricing model', render: (c) => <VPricingCell model={c.pricing_model} billing={c.billing} /> },
    { label: 'Price',         render: (c) => <VPriceCell price={c.price} currency={c.currency} pricingModel={c.pricing_model} /> },
    { label: 'Billing',       render: (c) => c.billing ? <span className="text-sm text-gray-700">{capitalize(c.billing)}</span> : <Minus className="w-4 h-4 text-gray-300 mx-auto" /> },
    { label: 'Free trial',    render: (c) => <VBoolCell value={c.has_free_trial} /> },
    { label: 'Trial length',  render: (c) => c.trial_days ? <span className="text-sm text-gray-700">{c.trial_days} days</span> : <Minus className="w-4 h-4 text-gray-300 mx-auto" /> },
  ];

  // Build feature rows from all plan features
  const featureOrder = {}; // slug -> { name, sort_order }
  const planFeatureMap = {}; // planId -> { featureSlug -> is_available }
  for (const plan of plans) {
    planFeatureMap[plan.id] = {};
    for (const feat of planFeatures[plan.id] ?? []) {
      featureOrder[feat.feature_slug] = { name: feat.feature_name, sort_order: feat.sort_order };
      planFeatureMap[plan.id][feat.feature_slug] = feat.is_available;
    }
  }
  const featureRows = Object.entries(featureOrder)
    .sort((a, b) => a[1].sort_order - b[1].sort_order)
    .map(([slug, { name }]) => ({
      label: name,
      isFeature: true,
      render: (col) => {
        if (col.tool_type === 'product') return <Minus className="w-4 h-4 text-gray-300 mx-auto" />;
        return <VBoolCell value={planFeatureMap[col.id]?.[slug] ?? null} />;
      },
    }));

  const hasFeatures = featureRows.length > 0;
  const dividerRow = hasFeatures
    ? [{ label: 'FEATURES', isDivider: true, render: () => null }]
    : [];
  const rows = [...pricingRows, ...dividerRow, ...featureRows];

  const colCount = columns.length;
  // Label col fixed, tool cols min 160px so it scrolls gracefully
  const templateCols = `140px repeat(${colCount}, minmax(160px, 1fr))`;

  return (
    <div className="space-y-3">
      {/* Column toggle tabs */}
      <div className="flex flex-wrap gap-2">
        {allColumns.map((col) => {
          const hidden = hiddenIds.has(col.id);
          return (
            <button
              key={col.id}
              onClick={() => toggleCol(col.id)}
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors
                ${hidden
                  ? 'bg-white border-gray-200 text-gray-400 hover:border-gray-400 hover:text-gray-600'
                  : 'bg-gray-900 border-gray-900 text-white hover:bg-gray-700'
                }`}
            >
              {hidden
                ? <><EyeOff className="w-3 h-3" />{col.name}</>
                : <><Eye className="w-3 h-3" />{col.name}</>
              }
            </button>
          );
        })}
      </div>

      {/* Comparison grid */}
      {columns.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">Select tools above to compare.</p>
      ) : (
    <div
      ref={scrollRef}
      className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm bg-white cursor-grab"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      <div className="grid min-w-max" style={{ gridTemplateColumns: templateCols }}>

        {/* Sticky header row */}
        <div className="px-5 py-4 bg-gray-50 border-b border-gray-200 flex items-end">
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest">Variations</p>
        </div>
        {columns.map((col) => {
          const inCompare  = compareList.includes(col.slug);
          const compareFull = compareList.length >= 3 && !inCompare;
          return (
            <div key={col.id} className="px-4 py-4 bg-gray-50 border-b border-l border-gray-200 flex flex-col items-center gap-2 text-center">
              <p className="text-sm font-semibold text-gray-900 leading-snug">{col.name}</p>
              {col.is_primary && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-900 text-white font-medium">Most popular</span>
              )}
              {/* Add-to-compare button */}
              <button
                onClick={() => !compareFull && onToggleCompare(col.slug)}
                disabled={compareFull}
                title={inCompare ? 'Remove from compare' : compareFull ? 'Max 3 tools selected' : 'Add to compare'}
                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors
                  ${inCompare
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : compareFull
                    ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'bg-white border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50'
                  }`}
              >
                {inCompare ? <><Check className="w-3 h-3" />In compare</> : <><Plus className="w-3 h-3" />Compare</>}
              </button>
            </div>
          );
        })}

        {/* Data rows */}
        {rows.map((row, i) => {
          if (row.isDivider) return (
            <div key="divider-features" className="contents">
              <div className="col-span-full px-5 py-2 bg-gray-100 border-t border-gray-200 flex items-center"
                style={{ gridColumn: `1 / span ${columns.length + 1}` }}>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Features</p>
              </div>
            </div>
          );
          return (
            <div key={row.label} className="contents">
              <div className={`px-5 py-3.5 flex items-center text-sm text-gray-500 border-t border-gray-100 ${i % 2 !== 0 ? 'bg-gray-50/60' : 'bg-white'}`}>
                {row.label}
              </div>
              {columns.map((col) => (
                <div key={col.id} className={`px-4 py-3.5 flex items-center justify-center border-t border-l border-gray-100 ${i % 2 !== 0 ? 'bg-gray-50/60' : 'bg-white'}`}>
                  {row.render(col)}
                </div>
              ))}
            </div>
          );
        })}

      </div>
    </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Sidebar pricing card — shows min plan price when plans exist
// ---------------------------------------------------------------------------
const SidebarPricingCard = ({ tool, plans = [], onVisit }) => {
  // All pricing for plans comes from tool_meta
  const planPrices = plans
    .map((p) => p.tool_meta?.price_from)
    .filter((v) => v != null);
  const minPlanPrice = planPrices.length ? Math.min(...planPrices) : null;

  const hasPlans     = plans.length > 0;
  const displayPrice = hasPlans ? minPlanPrice : tool.price_from;
  const displayCurrency = hasPlans
    ? (plans.find((p) => p.tool_meta?.price_from === minPlanPrice)?.tool_meta?.price_currency ?? 'USD')
    : (tool.price_currency ?? 'USD');

  const effectiveModel = resolveModel(tool.pricing_model, tool.billing_cycle);
  const badge    = PRICING_BADGE[effectiveModel] ?? 'bg-gray-100 text-gray-600 border-gray-200';
  const label    = PRICING_LABEL[effectiveModel] ?? effectiveModel;
  const hasTrial = hasPlans
    ? plans.some((p) => p.tool_meta?.has_free_trial)
    : tool.has_free_trial;

  const isFreeModel = tool.pricing_model === 'free' || tool.pricing_model === 'freemium';
  const isPaidModel = tool.pricing_model && !isFreeModel;
  const showContactSales = isPaidModel && (displayPrice == null || displayPrice === 0);
  const showFree         = isFreeModel && (displayPrice == null || displayPrice === 0);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm space-y-3">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Pricing</p>

      {label && (
        <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${badge}`}>
          {label}
        </span>
      )}

      {(displayPrice != null || showContactSales || showFree) && (
        <div>
          {showContactSales ? (
            <p className="text-xl font-bold text-amber-700">Contact Sales</p>
          ) : showFree || displayPrice === 0 ? (
            <p className="text-xl font-bold text-emerald-600">Free</p>
          ) : (
            <p className="text-xl font-bold text-gray-900">
              {hasPlans && <span className="text-sm font-normal text-gray-400 mr-0.5">from </span>}
              {displayCurrency} {Number(displayPrice).toLocaleString()}
              <span className="text-sm font-normal text-gray-400">/yr</span>
            </p>
          )}
          {hasPlans && (
            <p className="text-xs text-gray-400 mt-0.5">{plans.length} plan{plans.length !== 1 ? 's' : ''} available</p>
          )}
        </div>
      )}

      {hasTrial && (
        <p className="flex items-center gap-1.5 text-sm text-emerald-600">
          <Check className="w-3.5 h-3.5" />
          {!hasPlans && tool.trial_days ? `${tool.trial_days}-day ` : ''}Free trial available
        </p>
      )}

      {tool.website_url && (
        <a
          href={tool.affiliate_url ?? tool.website_url}
          target="_blank" rel="noopener noreferrer"
          onClick={onVisit}
          className="flex items-center justify-center gap-1.5 w-full py-2.5 rounded-xl bg-gray-900 hover:bg-gray-700 text-white text-sm font-semibold transition-colors"
        >
          Visit {tool.name}
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Review overlay — full form in a scrollable dialog
// ---------------------------------------------------------------------------
const CheckboxField = ({ checked, onChange, label }) => (
  <label className="flex items-start gap-2.5 cursor-pointer group" onClick={onChange}>
    <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
      checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white group-hover:border-blue-400'
    }`}>
      {checked && <Check className="w-3 h-3 text-white" />}
    </div>
    <span className="text-sm text-gray-600 leading-snug select-none">{label}</span>
  </label>
);

const INITIAL_FORM = {
  rating: 0, title: '', body: '', pros: '', cons: '',
  name: '', jobTitle: '', company: '', email: '',
  wantVerification: true, newsletterOptIn: true,
  googleVerified: false,
  planId: null,
};

const GoogleIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const ReviewOverlay = ({ open, onOpenChange, toolName, plans = [], onSubmit, submitState }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [googleUser, setGoogleUser] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  // Detect already-signed-in Firebase user when overlay opens
  useEffect(() => {
    if (!open) return;
    const unsubscribe = onAuthStateChanged(firebaseAuth, (fbUser) => {
      if (fbUser?.email) {
        setGoogleUser(fbUser);
        setForm((p) => ({
          ...p,
          email: fbUser.email,
          name: p.name || fbUser.displayName || '',
          wantVerification: false,
          googleVerified: true,
        }));
      }
    });
    return () => unsubscribe();
  }, [open]);

  // Reset form & google state each time the dialog opens
  const handleOpenChange = (v) => {
    if (v) {
      setForm(INITIAL_FORM);
      setGoogleUser(null);
      setGoogleError('');
    }
    onOpenChange(v);
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setGoogleError('');
    try {
      const result = await signInWithPopup(firebaseAuth, googleProvider);
      const fbUser = result.user;
      setGoogleUser(fbUser);
      setForm((p) => ({
        ...p,
        email: fbUser.email,
        name: p.name || fbUser.displayName || '',
        wantVerification: false,
        googleVerified: true,
      }));
    } catch (err) {
      const code = err.code ?? '';
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        setGoogleError('Google sign-in failed. Please try again or enter your email manually.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const canSubmit = form.rating > 0 && (form.email.trim() !== '' || googleUser != null);

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />

        {/* Slide-in panel */}
        <Dialog.Content
          className="fixed z-50 inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl flex flex-col focus:outline-none sm:inset-y-4 sm:right-4 sm:rounded-2xl sm:border sm:border-gray-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
            <div>
              <Dialog.Title className="text-base font-semibold text-gray-900">Write a review</Dialog.Title>
              <p className="text-xs text-gray-400 mt-0.5">{toolName}</p>
            </div>
            <Dialog.Close className="text-gray-400 hover:text-gray-700 transition-colors p-1.5 rounded-lg hover:bg-gray-100">
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

            {/* Plan selector — only shown when the product has child variations */}
            {plans.length > 0 && (
              <div>
                <label className="text-xs text-gray-500 font-medium mb-1 block">
                  Which version are you reviewing?
                </label>
                <select
                  value={form.planId ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, planId: e.target.value || null }))}
                  className="w-full px-3 py-2 text-sm rounded-xl bg-gray-100 border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-gray-900 transition-all appearance-none"
                >
                  <option value="">Overall product</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Rating */}
            <div>
              <label className="text-xs text-gray-500 font-medium mb-2 block">
                Your rating <span className="text-red-400">*</span>
              </label>
              <StarPicker value={form.rating} onChange={(r) => setForm((p) => ({ ...p, rating: r }))} />
            </div>

            {/* Headline */}
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1 block">Review headline</label>
              <input type="text" placeholder="Summarise your experience"
                value={form.title} onChange={set('title')}
                className="w-full px-3 py-2 text-sm rounded-xl bg-gray-100 border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-gray-900 placeholder-gray-400 transition-all"
              />
            </div>

            {/* Body */}
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1 block">Detailed review</label>
              <textarea rows={4} placeholder="Share your experience with this tool..."
                value={form.body} onChange={set('body')}
                className="w-full px-3 py-2 text-sm rounded-xl bg-gray-100 border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-gray-900 placeholder-gray-400 resize-none transition-all"
              />
            </div>

            {/* Pros / Cons */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 font-medium mb-1 block">Pros</label>
                <textarea rows={3} placeholder="What works well?"
                  value={form.pros} onChange={set('pros')}
                  className="w-full px-3 py-2 text-sm rounded-xl bg-emerald-50 border border-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:bg-white text-gray-900 placeholder-gray-400 resize-none transition-all"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium mb-1 block">Cons</label>
                <textarea rows={3} placeholder="What could be improved?"
                  value={form.cons} onChange={set('cons')}
                  className="w-full px-3 py-2 text-sm rounded-xl bg-red-50 border border-red-100 focus:outline-none focus:ring-2 focus:ring-red-300 focus:bg-white text-gray-900 placeholder-gray-400 resize-none transition-all"
                />
              </div>
            </div>

            {/* Identity section */}
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                Your identity <span className="text-red-400">*</span>
              </p>

              {/* Google signed-in pill */}
              {googleUser ? (
                <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <GoogleIcon />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{googleUser.displayName || 'Google account'}</p>
                      <p className="text-xs text-gray-500 truncate">{googleUser.email}</p>
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium shrink-0">
                    <Check className="w-3 h-3" />Verified
                  </span>
                </div>
              ) : (
                <>
                  {/* Sign in with Google */}
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={googleLoading}
                    className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-xl bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 text-gray-700 text-sm font-medium transition-colors disabled:opacity-50 shadow-sm"
                  >
                    {googleLoading
                      ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      : <GoogleIcon />}
                    {googleLoading ? 'Signing in…' : 'Continue with Google'}
                  </button>

                  {googleError && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />{googleError}
                    </p>
                  )}

                  {/* Divider */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400">or enter your email</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {/* Manual email */}
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">
                      Email <span className="text-red-400">*</span>
                    </label>
                    <input type="email" placeholder="you@example.com"
                      value={form.email} onChange={set('email')}
                      className="w-full px-3 py-2 text-sm rounded-xl bg-gray-100 border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-gray-900 placeholder-gray-400 transition-all"
                    />
                    <p className="text-xs text-gray-400 mt-1">Used for verification only — never shown publicly.</p>
                  </div>
                </>
              )}

              {/* Name / title / company — always optional */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">Name (optional)</label>
                  <input type="text" placeholder="Your name"
                    value={form.name} onChange={set('name')}
                    className="w-full px-3 py-2 text-sm rounded-xl bg-gray-100 border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-gray-900 placeholder-gray-400 transition-all"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">Job title (optional)</label>
                  <input type="text" placeholder="e.g. Structural Engineer"
                    value={form.jobTitle} onChange={set('jobTitle')}
                    className="w-full px-3 py-2 text-sm rounded-xl bg-gray-100 border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-gray-900 placeholder-gray-400 transition-all"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500 font-medium mb-1 block">Company (optional)</label>
                  <input type="text" placeholder="Your company"
                    value={form.company} onChange={set('company')}
                    className="w-full px-3 py-2 text-sm rounded-xl bg-gray-100 border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white text-gray-900 placeholder-gray-400 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Opt-in checkboxes — hide verification if Google (already verified) */}
            <div className="space-y-2.5">
              {!googleUser && (
                <CheckboxField
                  checked={form.wantVerification}
                  onChange={() => setForm((p) => ({ ...p, wantVerification: !p.wantVerification }))}
                  label="Send me a verification link to confirm this review"
                />
              )}
              <CheckboxField
                checked={form.newsletterOptIn}
                onChange={() => setForm((p) => ({ ...p, newsletterOptIn: !p.newsletterOptIn }))}
                label="Subscribe me to the AEC tech newsletter"
              />
            </div>

            {submitState === 'error' && (
              <p className="text-xs text-red-600 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" />Failed to submit. Please try again.
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex items-center justify-between gap-2">
            <p className="text-xs text-gray-400">
              {!canSubmit && form.rating > 0 && 'Email or Google sign-in required'}
            </p>
            <div className="flex items-center gap-2">
              <Dialog.Close className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors">
                Cancel
              </Dialog.Close>
              <button
                onClick={() => onSubmit(form)}
                disabled={!canSubmit || submitState === 'submitting'}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gray-900 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
              >
                {submitState === 'submitting'
                  ? <><Loader2 className="w-4 h-4 animate-spin" />Submitting…</>
                  : 'Post review'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const AECToolDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { compareList, toggleCompare } = useCompareList();

  const [tool, setTool]             = useState(null);
  const [plans, setPlans]           = useState([]);
  const [features, setFeatures]     = useState([]);
  const [planFeatures, setPlanFeatures] = useState({});
  const [reviews, setReviews]       = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  // Sticky compare bar visibility
  const heroRef        = useRef(null);
  const reviewSectionRef = useRef(null);
  const [showStickyBar, setShowStickyBar] = useState(false);

  // Review overlay
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [submitState, setSubmitState] = useState('idle'); // idle | submitting | success | error
  const [postSuccess, setPostSuccess] = useState(false);

  // Collapsible feature categories
  const [openCategories, setOpenCategories] = useState({});

  useEffect(() => {
    if (!slug) return navigate('/library/aec', { replace: true });
    setLoading(true);
    setError(null);

    fetchToolBySlug(slug)
      .then(async (t) => {
        setTool(t);
        trackEvent(t.id, 'tool_view');
        const [feats, revs, cats, plns] = await Promise.all([
          fetchToolFeatures(t.id).catch((e) => { console.error('[fetchToolFeatures]', e); return []; }),
          fetchToolReviews(t.id).catch((e) => { console.error('[fetchToolReviews]', e); return []; }),
          fetchToolCategories(t.id).catch((e) => { console.error('[fetchToolCategories]', e); return []; }),
          fetchToolPlans(t.id).catch((e) => { console.error('[fetchToolPlans]', e); return []; }),
        ]);
        setFeatures(feats);
        setReviews(revs);
        setCategories(cats);
        setPlans(plns);
        const planIds = plns.map((p) => p.id);
        fetchToolFeaturesMany(planIds)
          .then(setPlanFeatures)
          .catch((e) => console.error('[fetchToolFeaturesMany]', e));
        const catNames = [...new Set(feats.map((f) => f.feature_category ?? 'General'))];
        setOpenCategories(Object.fromEntries(catNames.map((c) => [c, true])));
      })
      .catch(() => setError('Tool not found or failed to load.'))
      .finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver for sticky bar
  useEffect(() => {
    if (!heroRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-56px 0px 0px 0px' }
    );
    observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, [tool]);

  const handleVisitSite = () => {
    if (tool) trackEvent(tool.id, 'tool_click');
  };

  const handleToggleCompare = (slugOverride) => {
    if (!tool) return;
    // VariationsTable passes a child plan slug; sidebar/sticky pass no argument → use parent slug.
    const targetSlug = typeof slugOverride === 'string' ? slugOverride : tool.slug;
    const inCompare = compareList.includes(targetSlug);
    if (!inCompare && compareList.length < 3) {
      trackEvent(tool.id, 'tool_compare');
    }
    toggleCompare(targetSlug);
  };

  const handleOpenDialog = () => {
    setSubmitState('idle');
    setDialogOpen(true);
  };

  const handleSubmitReview = async (form) => {
    setSubmitState('submitting');
    try {
      await submitToolReview({
        toolId:          tool.id,
        reviewerName:    form.name        || null,
        reviewerJobTitle:form.jobTitle    || null,
        reviewerCompany: form.company     || null,
        reviewerEmail:   form.email       || null,
        rating:          form.rating,
        title:           form.title       || null,
        body:            form.body        || null,
        pros:            form.pros        || null,
        cons:            form.cons        || null,
        newsletterOptIn: form.newsletterOptIn,
        wantVerification:form.wantVerification,
        googleVerified:  form.googleVerified ?? false,
        planId:          form.planId      || null,
      });
      setSubmitState('success');
      setDialogOpen(false);
      setPostSuccess(true);
    } catch (err) {
      console.error('[handleSubmitReview]', err?.message ?? err);
      setSubmitState('error');
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" />
        <p className="text-sm">Loading tool profile...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4 text-center px-6">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
          <AlertCircle className="w-7 h-7 text-gray-400" />
        </div>
        <p className="text-base font-semibold text-gray-900">{error}</p>
        <Link to="/library/aec" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
          Back to directory
        </Link>
      </div>
    </div>
  );

  // ── Derived data ─────────────────────────────────────────────────────────
  const featuresByCategory = features.reduce((acc, f) => {
    const cat = f.feature_category ?? 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});

  const featureCategoryNames = Object.keys(featuresByCategory);
  // For parent tools (have plans), inCompare tracks whether any child plan slug is in the list.
  // For standalone tools (no plans), track the tool's own slug.
  const inCompare   = plans.length > 0
    ? plans.some((p) => compareList.includes(p.slug))
    : compareList.includes(tool.slug);
  const compareFull = compareList.length >= 3;
  const integrations = tool.tool_meta?.integrations?.length
    ? tool.tool_meta.integrations
    : tool.integrations ?? [];

  const verifiedReviews = reviews.filter((r) => r.status === 'published_verified');
  const anonReviews     = reviews.filter((r) => r.status === 'published_anonymous');


  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Sticky top nav ── */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div className="px-4 sm:px-6 h-14 flex items-center gap-3">
          <Link
            to="/library/aec"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            AEC Tools
          </Link>
          <span className="text-gray-300">/</span>
          <p className="text-sm text-gray-900 font-medium truncate">{tool.name}</p>
        </div>
      </header>

      {/* ── Sticky compare bar (appears after hero scrolls out) ── */}
      {showStickyBar && (
        <div className="sticky top-14 z-20 bg-white/90 backdrop-blur-xl border-b border-gray-200 shadow-sm">
          <div className="px-4 sm:px-6 h-12 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {tool.logo_url ? (
                <img src={tool.logo_url} alt={tool.name}
                  className="w-7 h-7 rounded-lg object-contain bg-gray-100 p-0.5 border border-gray-200 shrink-0" />
              ) : (
                <HardHat className="w-5 h-5 text-gray-400 shrink-0" />
              )}
              <p className="text-sm font-semibold text-gray-900 truncate">{tool.name}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {compareList.length >= 2 && (
                <button
                  onClick={() => navigate(`/library/aec/compare/${compareList.join('-vs-')}`)}
                  className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-gray-900 hover:bg-gray-700 text-white font-medium transition-colors"
                >
                  <GitCompare className="w-3.5 h-3.5" />
                  Compare {compareList.length}
                </button>
              )}
              {plans.length > 0 ? (
                // Parent tool — nudge user to pick a plan from the table below
                <button
                  onClick={() => document.getElementById('variations')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:border-blue-400 hover:text-blue-600 font-medium transition-colors"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                  Pick a plan
                </button>
              ) : (
                // Standalone tool — add directly to compare
                <button
                  onClick={handleToggleCompare}
                  disabled={compareFull}
                  className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border font-medium transition-colors
                    ${inCompare
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : compareFull
                      ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-600'
                    }`}
                >
                  {inCompare
                    ? <><Check className="w-3.5 h-3.5" />In compare</>
                    : <><GitCompare className="w-3.5 h-3.5" />Add to compare</>
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 pb-20">

        {/* ── Hero card ── */}
        <div ref={heroRef} className="mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row gap-6">

            {/* Logo — click opens vendor website */}
            <a
              href={tool.affiliate_url ?? tool.website_url ?? '#'}
              target="_blank" rel="noopener noreferrer"
              onClick={handleVisitSite}
              title={`Visit ${tool.name} website`}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden shrink-0 hover:ring-2 hover:ring-gray-300 transition-all"
            >
              {tool.logo_url ? (
                <img src={tool.logo_url} alt={tool.name} className="w-full h-full object-contain p-2" />
              ) : (
                <HardHat className="w-10 h-10 text-gray-400" />
              )}
            </a>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-start gap-2 mb-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight leading-tight">
                  {tool.name}
                </h1>
                {tool.pricing_model && (
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border self-start mt-1 ${PRICING_BADGE[tool.pricing_model] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                    {PRICING_LABEL[tool.pricing_model] ?? tool.pricing_model}
                  </span>
                )}
              </div>

              {tool.vendor_name && (
                <p className="text-sm text-gray-400 mb-2">by {tool.vendor_name}</p>
              )}

              {tool.tagline && (
                <p className="text-base text-gray-600 leading-relaxed mb-3">{tool.tagline}</p>
              )}

              {/* Rating */}
              {tool.avg_rating > 0 && (
                <div className="mb-3">
                  <StarRating rating={tool.avg_rating} count={tool.review_count} size="lg" />
                </div>
              )}

              {/* Categories */}
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {categories.map((cat) => (
                    <span key={cat.id}
                      className="text-xs px-2.5 py-1 rounded-full bg-gray-100 border border-gray-200 text-gray-600">
                      {cat.name}
                    </span>
                  ))}
                </div>
              )}

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-2 mt-auto">
                <button
                  onClick={() => navigate(
                    compareList.length >= 2
                      ? `/library/aec/compare/${compareList.join('-vs-')}`
                      : '/library/aec/compare'
                  )}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-900 hover:bg-gray-700 text-white text-sm font-semibold transition-colors"
                >
                  <GitCompare className="w-3.5 h-3.5" /> Compare
                  {compareList.length >= 2 && (
                    <span className="ml-0.5 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">{compareList.length}</span>
                  )}
                </button>
                <Link
                  to={`/contact?mode=aec&tool=${tool.slug}`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-semibold transition-colors"
                >
                  <Briefcase className="w-3.5 h-3.5" /> Get implementation support
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ── Main content ── */}
        <div className="mt-6 space-y-6">

            {/* About + Pricing */}
            {(tool.description_long || tool.description_short || tool.pricing_model) && (
              <SectionCard id="about" title="About" icon={Building2}>
                {(tool.description_long || tool.description_short) && (
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                    {tool.description_long || tool.description_short}
                  </p>
                )}

                {/* Inline pricing */}
                {tool.pricing_model && (
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Pricing</p>
                    <div className="flex flex-wrap items-center gap-3">
                      {(() => {
                        const m = resolveModel(tool.pricing_model, tool.billing_cycle);
                        const badge = PRICING_BADGE[m] ?? 'bg-gray-100 text-gray-600 border-gray-200';
                        const label = PRICING_LABEL[m] ?? m;
                        const planPrices = plans.map((p) => p.tool_meta?.price_from).filter((v) => v != null);
                        const minPrice = planPrices.length ? Math.min(...planPrices) : tool.price_from;
                        const currency = tool.price_currency ?? 'USD';
                        const isFree = tool.pricing_model === 'free' || tool.pricing_model === 'freemium';
                        const hasTrial = plans.length > 0
                          ? plans.some((p) => p.tool_meta?.has_free_trial)
                          : tool.has_free_trial;
                        return (
                          <>
                            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${badge}`}>{label}</span>
                            {minPrice > 0 ? (
                              <span className="text-sm font-semibold text-gray-900">
                                {plans.length > 0 && <span className="text-xs font-normal text-gray-400 mr-0.5">from </span>}
                                {currency} {Number(minPrice).toLocaleString()}<span className="text-xs font-normal text-gray-400">/yr</span>
                              </span>
                            ) : isFree ? (
                              <span className="text-sm font-semibold text-emerald-600">Free</span>
                            ) : tool.pricing_model ? (
                              <span className="text-sm font-semibold text-amber-700">Contact Sales</span>
                            ) : null}
                            {hasTrial && (
                              <span className="flex items-center gap-1 text-sm text-emerald-600">
                                <Check className="w-3.5 h-3.5" />
                                {!plans.length && tool.trial_days ? `${tool.trial_days}-day ` : ''}Free trial
                              </span>
                            )}
                            {plans.length > 0 && (
                              <span className="text-xs text-gray-400">{plans.length} plan{plans.length !== 1 ? 's' : ''} available</span>
                            )}
                            {tool.pricing_page_url && (
                              <a href={tool.pricing_page_url} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-0.5 text-xs text-blue-600 hover:underline">
                                View pricing <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </SectionCard>
            )}

            {/* Features */}
            {features.length > 0 && (
              <SectionCard id="features" title="Features" icon={Zap}>
                <div className="space-y-4">
                  {featureCategoryNames.map((cat) => {
                    const isOpen = openCategories[cat] !== false;
                    return (
                      <div key={cat} className="border border-gray-100 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setOpenCategories((prev) => ({ ...prev, [cat]: !isOpen }))}
                          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                        >
                          <span className="text-sm font-semibold text-gray-700">{cat}</span>
                          {isOpen
                            ? <ChevronUp className="w-4 h-4 text-gray-400" />
                            : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </button>
                        {isOpen && (
                          <div className="divide-y divide-gray-100">
                            {featuresByCategory[cat].map((feature) => (
                              <div key={feature.id}
                                className="flex items-center justify-between gap-4 px-4 py-3">
                                <span className="text-sm text-gray-600 flex-1">{feature.feature_name}</span>
                                <div className="shrink-0">
                                  <FeatureValue feature={feature} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            )}

            {/* Variations comparison table */}
            {plans.length > 0 && (
              <SectionCard id="variations" title="Plans & Variations" icon={Layers}>
                <VariationsTable
                  tool={tool}
                  plans={plans}
                  planFeatures={planFeatures}
                  compareList={compareList}
                  onToggleCompare={handleToggleCompare}
                />
              </SectionCard>
            )}

            {/* Integrations */}
            {integrations.length > 0 && (
              <SectionCard id="integrations" title="Integrations" icon={GitCompare}>
                <div className="flex flex-wrap gap-2">
                  {integrations.map((item) => (
                    <span key={item}
                      className="text-sm px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700">
                      {item}
                    </span>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Reviews */}
            <SectionCard
              id="reviews"
              cardRef={reviewSectionRef}
              title={`Reviews${reviews.length ? ` (${reviews.length})` : ''}`}
              icon={MessageSquare}
            >
              {/* Success banner */}
              {postSuccess && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm mb-5">
                  <Check className="w-4 h-4 shrink-0" />
                  Thanks! Your review has been submitted and is pending moderation. Check your email for a verification link if you provided one.
                </div>
              )}

              {/* Verified reviews */}
              {verifiedReviews.length > 0 && (
                <div className="space-y-3 mb-4">
                  {verifiedReviews.map((r) => <ReviewCard key={r.id} review={r} />)}
                </div>
              )}

              {/* Separator */}
              {verifiedReviews.length > 0 && anonReviews.length > 0 && (
                <p className="text-xs text-gray-400 mb-3">Community reviews</p>
              )}

              {/* Anonymous reviews */}
              {anonReviews.length > 0 && (
                <div className="space-y-3">
                  {anonReviews.map((r) => <ReviewCard key={r.id} review={r} />)}
                </div>
              )}

              {reviews.length === 0 && !postSuccess && (
                <p className="text-sm text-gray-400">No reviews yet. Be the first!</p>
              )}

              {/* Write a review CTA */}
              {!postSuccess && (
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleOpenDialog}
                    className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gray-900 hover:bg-gray-700 text-white text-sm font-semibold transition-colors"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Write a review
                  </button>
                </div>
              )}
            </SectionCard>

            {/* Consulting CTA */}
            <div className="bg-gray-900 rounded-2xl p-6 sm:p-8 text-white">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1">Need help implementing {tool.name}?</h3>
                  <p className="text-sm text-gray-400 leading-relaxed mb-4">
                    Our AEC technology consultants can help you evaluate, procure, and deploy the right tools for your team.
                  </p>
                  <Link
                    to={`/contact?mode=aec&tool=${tool.slug}`}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-white text-gray-900 text-sm font-semibold hover:bg-gray-100 transition-colors"
                  >
                    Get consulting support
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>

        </div>
      </div>

      {/* Review overlay */}
      <ReviewOverlay
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        toolName={tool.name}
        plans={plans}
        onSubmit={handleSubmitReview}
        submitState={submitState}
      />
    </div>
  );
};

export default AECToolDetail;
